import React, { useEffect, useState } from "react";
import { api, PROGRAM_LABELS, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const PLACEHOLDERS = ["{name}", "{email}", "{program_name}", "{event_datetime}", "{timezone}", "{zoom_link}", "{payment_symbol}", "{payment_amount}", "{booking_id}", "{batch_name}", "{coach_name}", "{start_date}", "{session_time}"];

const TRIGGERS = ["booking_confirmed","payment_successful","payment_pending","payment_failed","reminder_24h","reminder_1h","reminder_10min","session_completed","followup_upsell"];

export default function EmailTemplatesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({name:"", program_type:"meditation_enlightenment", trigger:"booking_confirmed", subject:"", body:"", channel:"email", status:"active"});
  const [editingId, setEditingId] = useState(null);
  const [previewMode, setPreviewMode] = useState("edit");

  const load = async () => { const {data} = await api.get("/admin/email-templates"); setItems(data); };
  useEffect(()=>{load();},[]);

  const upd = (k,v) => setForm(s=>({...s,[k]:v}));
  const insertPh = (ph) => upd("body", (form.body||"") + ph);

  const save = async () => {
    try { if (editingId) await api.patch(`/admin/email-templates/${editingId}`, form); else await api.post("/admin/email-templates", form);
      toast.success("Saved"); setOpen(false); load();
    } catch(e){ toast.error(formatErr(e)); }
  };
  const test = async () => {
    const recipient = window.prompt("Send test email to:", "test@example.com");
    if (!recipient) return;
    try { await api.post("/admin/email-templates/test-send", { recipient, template_id: editingId }); toast.success("Test logged (mock send) — see Email Logs"); }
    catch(e){ toast.error(formatErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/email-templates/${id}`); load(); };

  return (
    <div className="space-y-6 fade-up" data-testid="admin-templates">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-4xl">Email Templates</h1>
        <button onClick={()=>{setForm({name:"", program_type:"meditation_enlightenment", trigger:"booking_confirmed", subject:"", body:"", channel:"email", status:"active"}); setEditingId(null); setOpen(true);}} data-testid="new-template" className="btn-primary">+ New template</button>
      </div>

      <div className="soft-card !p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3EFEA] text-xs uppercase tracking-wider text-[#7E736E]">
            <tr><th className="text-left p-4">Name</th><th className="text-left">Program</th><th className="text-left">Trigger</th><th className="text-left">Status</th><th className="text-left">Updated</th><th/></tr>
          </thead>
          <tbody>
            {items.map(t=>(
              <tr key={t.id} className="border-t border-[#EBE5D9]" data-testid={`tpl-${t.id}`}>
                <td className="p-4">{t.name}</td>
                <td className="text-xs">{PROGRAM_LABELS[t.program_type] || t.program_type}</td>
                <td className="text-xs">{t.trigger}</td>
                <td><span className="badge-soft bg-[#F3EFEA] text-[#5C5955]">{t.status}</span></td>
                <td className="text-xs text-[#7E736E]">{(t.updated_at||"").slice(0,16).replace("T"," ")}</td>
                <td className="text-right pr-4">
                  <button onClick={()=>{setForm({...t}); setEditingId(t.id); setOpen(true);}} className="text-xs text-terracotta underline mr-3">Edit</button>
                  <button onClick={()=>del(t.id)} className="text-xs text-red-700 underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tpl-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editingId?"Edit template":"New template"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={e=>upd("name",e.target.value)}/></div>
            <div><Label>Trigger</Label>
              <Select value={form.trigger} onValueChange={v=>upd("trigger",v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{TRIGGERS.map(t=>(<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Program</Label>
              <Select value={form.program_type} onValueChange={v=>upd("program_type",v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  {Object.entries(PROGRAM_LABELS).map(([k,v])=>(<SelectItem key={k} value={k}>{v}</SelectItem>))}
                  <SelectItem value="payment">Payment</SelectItem><SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v=>upd("status",v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="paused">Paused</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Subject</Label><Input value={form.subject} onChange={e=>upd("subject",e.target.value)}/></div>
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-1">
                <Label>Body (HTML supported)</Label>
                <Tabs value={previewMode} onValueChange={setPreviewMode}>
                  <TabsList><TabsTrigger value="edit">Edit</TabsTrigger><TabsTrigger value="preview">Preview</TabsTrigger></TabsList>
                </Tabs>
              </div>
              {previewMode === "edit" ? (
                <Textarea rows={10} value={form.body} onChange={e=>upd("body",e.target.value)} className="font-mono text-xs"/>
              ) : (
                <div className="rounded-xl border border-[#EBE5D9] p-4 bg-white prose prose-sm max-w-none" dangerouslySetInnerHTML={{__html: form.body || "<em>Nothing to preview</em>"}}/>
              )}
              <div className="mt-2 flex flex-wrap gap-1">
                {PLACEHOLDERS.map(p=>(<button key={p} type="button" onClick={()=>insertPh(p)} className="text-[10px] px-2 py-1 rounded-full bg-[#F3EFEA] hover:bg-[#EBE5D9]">{p}</button>))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            {editingId && <button onClick={test} className="btn-ghost text-xs">Send Test</button>}
            <button onClick={save} className="btn-primary">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
